import { Clear, InfoOutlined, Search } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, InputAdornment, List, ListItem, ListItemButton, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { usePostProjectSearch } from 'src/apis/resourcePlannerAPI';
import elements from 'src/assets/styles/variables/colors/global/elements.module.scss';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from 'src/components/mui-components';
import { HighlightMatchingText } from 'src/components/utils/HighlightMatchingText';
import { ResponseHandler } from 'src/components/utils/ResponseHandler';
import { useDebounce } from 'use-debounce';
import { useGetFlattenedFilterItems, useKeyboardControl } from '../../components/AssignFlow/hooks';
import { FilterItem } from '../../components/AssignFlow/components/FilterItems/FilterItem';
import { defaultFilters } from './searchForProjectDialog.constants';
import styles from './SearchForProjectDialog.module.scss';
import { ETaskFilterNames, TTaskDefaultFilters } from './searchForProjectDialog.types';
import { SkeletonItem } from '../components';

export const SearchForProjectDialog = () => {
  const { t } = useTranslation('assignFlow');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name');
  const [projectQuery, setProjectQuery] = useState('');
  const [debouncedProjectQuery] = useDebounce(projectQuery, 500);
  const [selectedFilters, setSelectedFilters] =
    useState<Partial<TTaskDefaultFilters>>(defaultFilters);
  const taskFilterItems = useGetFlattenedFilterItems(ETaskFilterNames);

  const showDepartmentFilter = !!taskFilterItems.find(
    ({ filterName }) => filterName === 'ProjectDepartment',
  )?.id;
  const showLegalEntityFilter = !!taskFilterItems.find(
    ({ filterName }) => filterName === 'LegalEntityProject',
  )?.id;

  // Handle projects
  const {
    data: projectSearch,
    observerRef: projectSearchObserverRef,
    hasNextPage: projectSearchHasNextPage,
    isLoading: projectSearchIsLoading,
    isError: projectSearchIsError,
    isFetchingNextPage: projectSearchIsFetchingNextPage,
  } = usePostProjectSearch({
    customer: selectedFilters.Customer,
    department: selectedFilters.ProjectDepartment,
    legalEntity: selectedFilters.LegalEntityProject,
    projectManager: selectedFilters.ProjectManager,
    projectType: selectedFilters.ProjectType,
    query: debouncedProjectQuery,
  });

  const {
    dialogOnKeyDown,
    initialFocusElement,
    searchContainerResults,
    selectItemIndex,
    setSelectItemIndex,
    textFieldOnKeyDown,
  } = useKeyboardControl({
    hasLazyLoad: true,
    selector: 'a[data-project-link]',
  });

  const cleanUp = () => {
    navigate(-1);
    setProjectQuery('');
    setSelectedFilters(defaultFilters);
  };

  const dialogOnClose = () => {
    cleanUp();
  };

  return (
    <Dialog
      data-automation-id="SearchForTaskDialog"
      fullWidth
      maxWidth="md"
      onClose={dialogOnClose}
      onKeyDown={dialogOnKeyDown}
      open
      PaperProps={{ sx: { overflowY: 'unset' } }}
      slotProps={{
        backdrop: {
          transitionDuration: {
            exit: 0,
          },
        },
      }}
    >
      <DialogTitle component={Box} data-automation-id="SearchForTaskDialogTitle">
        <Stack gap={2}>
          <Typography lineHeight={1.6} variant="h2">
            <Trans
              i18nKey="AssignEmployeeToTaskHeader"
              key="AssignEmployeeToTaskHeader"
              defaults={t('AssignEmployeeToTaskHeader')}
              ns="assignFlow"
              values={{ employee: name, projectName: '' }}
              components={[<strong key={0}>{name}</strong>]}
            />
          </Typography>
          <Stack alignItems="center" direction="row" gap={2}>
            <TextField
              ariaLabel={t('SearchForProjectsInputLabel')}
              data-automation-id="SuperSearchForTasks"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {projectQuery ? (
                      <IconButton
                        data-automation-id="SearchForTasksInputClearButton"
                        onClick={() => setProjectQuery('')}
                        size="small"
                        title={t('ClearSearchButtonText')}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ) : null}
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                inputProps: { autoFocus: true, autoComplete: 'off' },
              }}
              inputRef={initialFocusElement}
              label={t('SearchForProjectsInputLabel')}
              onChange={(e) => setProjectQuery(e.target.value)}
              onKeyDown={textFieldOnKeyDown}
              value={projectQuery}
            />
            <Tooltip
              tabIndex={0}
              title={
                <Stack>
                  <Typography component="h3" fontWeight={700} lineHeight={1.5} variant="h4">
                    {t('SearchForProjectsTooltipHeading')}
                  </Typography>
                  <Typography variant="small" sx={{ whiteSpace: 'pre-line' }}>
                    {t('SearchForProjectsTooltip')}
                  </Typography>
                </Stack>
              }
            >
              <InfoOutlined />
            </Tooltip>
          </Stack>
          <Stack direction="row" flex={1} flexWrap="wrap">
            {taskFilterItems.map((i) => (
              <FilterItem
                {...i}
                data-automation-id={`FilterItem-${i.filterName}`}
                filterName={i.filterName}
                key={String(i.id)}
                onChange={(v) => {
                  setSelectedFilters((prev) => ({
                    ...prev,
                    [i.filterName]: v,
                  }));
                }}
              />
            ))}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers onMouseEnter={() => setSelectItemIndex(-1)} sx={{ pt: 2, zIndex: 0 }}>
        <ResponseHandler
          EmptyComponent={<Typography textAlign="center">{t('NoResults')}</Typography>}
          isError={projectSearchIsError}
          isEmpty={!projectSearch.length}
          isLoading={projectSearchIsLoading}
          LoadingComponent={
            <List className={styles.list} sx={{ minHeight: 460 }}>
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
            </List>
          }
        >
          <List className={styles.list} ref={searchContainerResults}>
            {projectSearch.map((p, i) => {
              if (p) {
                const { properties } = p;
                return (
                  <ListItemButton
                    key={properties.projectID}
                    component={Link}
                    to={`${properties.projectID}/tasks?name=${name}&projectName=${properties.projectName}`}
                    data-automation-id={`ProjectItem-${properties.projectID}`}
                    data-project-link
                    sx={{
                      backgroundColor: selectItemIndex === i ? elements.colorSelected : '',
                      borderBlockEnd: `1px solid ${elements.colorBorder}`,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Stack flex={1} gap={0}>
                      <Box>
                        <HighlightMatchingText
                          matchName={projectQuery}
                          name={properties.projectName}
                        />
                        {properties.projectNo ? (
                          <HighlightMatchingText
                            matchName={projectQuery}
                            name={` (${properties.projectNo})`}
                          />
                        ) : null}
                      </Box>
                      <Typography variant="small">
                        <HighlightMatchingText
                          matchName={projectQuery}
                          name={properties.customerName}
                        />
                        {properties.customerNo ? (
                          <HighlightMatchingText
                            matchName={projectQuery}
                            name={` (${properties.customerNo})`}
                          />
                        ) : null}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap">
                        <Typography className={styles.separatedLabels} variant="small">
                          {showDepartmentFilter && properties.legalEntityName ? (
                            <HighlightMatchingText
                              matchName={projectQuery}
                              name={properties.legalEntityName}
                            />
                          ) : null}
                          {showLegalEntityFilter && properties.departmentName ? (
                            <HighlightMatchingText
                              matchName={projectQuery}
                              name={properties.departmentName}
                            />
                          ) : null}
                          {properties.projectTypeName ? (
                            <HighlightMatchingText
                              matchName={projectQuery}
                              name={properties.projectTypeName}
                            />
                          ) : null}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                );
              }

              return null;
            })}
            {projectSearchHasNextPage && (
              <ListItem disablePadding ref={projectSearchObserverRef} data-filters>
                {projectSearchIsFetchingNextPage && <SkeletonItem />}
              </ListItem>
            )}
          </List>
        </ResponseHandler>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" flex={1} justifyContent="flex-end">
          <Button data-automation-id="CancelButton" onClick={cleanUp} variant="outlined">
            {t('CancelButtonText')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
